import { create } from "zustand";

import { api, ApiError, type DossierJob, downloadPdf } from "../api/client";
import { k, t, tServer } from "../i18n/i18n";
import { type DossierMissing, missingFromServer } from "./dossierState";

/** Genèse du dossier d'exercice (demande d'Alexis du 18/10/2026, registre F37) : « Quand l'utilisateur télécharge le
 *  dossier d'exercice, je veux un visuel spécifique lui indiquant que le dossier est en cours de genèse. »
 *
 *  Le dossier complet est produit par une tâche du serveur qui publie ses étapes (`POST …/dossier/export/jobs`) :
 *  l'interface les suit (`POLL_MS`), montre la vraie progression, puis enregistre le fichier produit. Tenue hors du
 *  menu : la genèse continue quand le menu se ferme, quand on change de vue ou de dossier ; après un rechargement de
 *  la page, la carte se rattache à la genèse en cours (`reattachGenesis`). Une genèse interrompue côté serveur
 *  (processus redémarré) est relancée une fois d'office ; un serveur momentanément injoignable est réinterrogé
 *  (`MAX_FAILURES` fois, en espaçant) avant l'échec, qui propose « Réessayer ».
 *
 *  Les documents séparés (fiche pays, mise en ambiance, OPO) se préparent en quelques secondes par leur route
 *  d'export : la même carte, en plus léger (sans étapes, barre indéterminée). */

/** Document préparé : le dossier complet, ou un document séparé du menu. */
export type GenesisKind = "dossier" | "country" | "part" | "oplan" | "order";

/** Phase : lancement, genèse (ou préparation) en cours, enregistrement du fichier, enregistré, échec. */
export type GenesisPhase = "starting" | "running" | "saving" | "saved" | "failed";

export interface Genesis {
  kind: GenesisKind;
  exerciseId: string;
  number: number;
  /** Nom du dossier (sous le titre de la carte). */
  name: string;
  phase: GenesisPhase;
  job: DossierJob | null;
  /** Début de l'attente (ms, horloge du poste) : temps écoulé affiché. */
  startedAt: number;
  /** Fin (enregistré ou échec) : le temps affiché s'arrête. */
  endedAt: number | null;
  filename: string | null;
  error: string | null;
  /** Échec à l'enregistrement d'un fichier déjà produit : « Réessayer » le réenregistre sans tout refaire. */
  failedAt: "job" | "save" | null;
  /** Carte réduite en pastille (« Réduire ») : la genèse continue. */
  compact: boolean;
  /** Document séparé : son adresse d'export. */
  url: string | null;
}

/** Délai entre deux lectures de l'état de la genèse. */
export const POLL_MS = 900;
/** Lectures en échec d'affilée tolérées (serveur redémarré, réseau coupé) avant d'annoncer l'échec. */
export const MAX_FAILURES = 8;
/** Durée d'affichage de « Dossier enregistré » avant que la carte ne s'efface d'elle-même. */
export const SAVED_MS = 8000;

/** Dépendances remplaçables par les essais. */
export const genesisDeps = {
  start: (exerciseId: string, number: number) => api.startDossierJob(exerciseId, number),
  read: (exerciseId: string, jobId: string) => api.dossierJob(exerciseId, jobId),
  current: (exerciseId: string) => api.currentDossierJob(exerciseId),
  fileUrl: (exerciseId: string, jobId: string) => api.dossierJobFileUrl(exerciseId, jobId),
  download: (url: string) => downloadPdf(url),
  sleep: (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
};

interface GenesisStore {
  genesis: Genesis | null;
}

export const useGenesis = create<GenesisStore>(() => ({ genesis: null }));

/** Suivi en cours : un nouveau lancement abandonne le précédent (sa boucle s'arrête d'elle-même). */
let run = 0;
let savedTimer: ReturnType<typeof setTimeout> | null = null;

function patch(mine: number, values: Partial<Genesis>): void {
  if (mine !== run) return;
  const genesis = useGenesis.getState().genesis;
  if (genesis) useGenesis.setState({ genesis: { ...genesis, ...values } });
}

/** Genèse active (le menu attend et n'en lance pas d'autre). */
export function genesisBusy(genesis: Genesis | null): boolean {
  return Boolean(genesis && (genesis.phase === "starting" || genesis.phase === "running" || genesis.phase === "saving"));
}

/** Téléchargement d'un document du menu : le nom du fichier enregistré, ou l'avis à montrer, ou le préalable
 *  manquant que le serveur a signalé. */
export async function fetchDossierFile(
  url: string,
): Promise<{ saved: string } | { missing: DossierMissing } | { failed: string }> {
  try {
    return { saved: await genesisDeps.download(url) };
  } catch (e) {
    const missing = incomplete(e);
    if (missing) return { missing };
    return { failed: e instanceof Error ? e.message : t("Le serveur n'a pas pu préparer le fichier. Réessayez dans un instant.") };
  }
}

function incomplete(e: unknown): DossierMissing | undefined {
  return e instanceof ApiError && e.code === "DOSSIER_INCOMPLETE" ? missingFromServer(e.detail?.missing, e.detail?.view) : undefined;
}

function begin(values: Pick<Genesis, "kind" | "exerciseId" | "number" | "name" | "url">, job: DossierJob | null = null): number {
  run += 1;
  if (savedTimer) clearTimeout(savedTimer);
  savedTimer = null;
  const now = genesisDeps.now();
  useGenesis.setState({
    genesis: {
      ...values,
      phase: job ? "running" : "starting",
      job,
      startedAt: job ? now - job.elapsed_s * 1000 : now,
      endedAt: null,
      filename: null,
      error: null,
      failedAt: null,
      compact: false,
    },
  });
  return run;
}

function saved(mine: number, filename: string): void {
  if (mine !== run) return;
  patch(mine, { phase: "saved", filename, endedAt: genesisDeps.now(), compact: false });
  savedTimer = setTimeout(() => {
    if (mine === run && useGenesis.getState().genesis?.phase === "saved") closeGenesis();
  }, SAVED_MS);
}

function failed(mine: number, error: string, failedAt: "job" | "save"): void {
  patch(mine, { phase: "failed", error, failedAt, endedAt: genesisDeps.now(), compact: false });
}

const LOST = k("Serveur injoignable : vérifiez la connexion du poste, puis réessayez.");
const INTERRUPTED = k("La préparation du dossier a été interrompue sur le serveur. Réessayez.");

/** Suit la genèse `job` jusqu'à sa fin, puis enregistre le fichier. */
async function follow(mine: number, exerciseId: string, number: number, first: DossierJob): Promise<void> {
  let job = first;
  let failures = 0;
  let relaunched = false;
  while (mine === run) {
    if (job.state === "done") {
      await save(mine, exerciseId, job);
      return;
    }
    if (job.state === "failed") {
      failed(mine, tServer(job.error ?? "") || t("Le serveur n'a pas pu préparer le fichier. Réessayez dans un instant."), "job");
      return;
    }
    if (job.state === "interrupted") {
      // Processus du serveur redémarré pendant la genèse : relancée une fois d'office.
      if (relaunched) {
        failed(mine, t(INTERRUPTED), "job");
        return;
      }
      relaunched = true;
      try {
        job = await genesisDeps.start(exerciseId, number);
        patch(mine, { job, phase: "running" });
        continue;
      } catch (e) {
        failed(mine, e instanceof Error ? e.message : t(INTERRUPTED), "job");
        return;
      }
    }
    await genesisDeps.sleep(POLL_MS * Math.min(1 + failures, 5));
    if (mine !== run) return;
    try {
      job = await genesisDeps.read(exerciseId, job.id);
      failures = 0;
      patch(mine, { job, phase: "running" });
    } catch (e) {
      // Genèse effacée (serveur nettoyé) : relancée comme une genèse interrompue.
      if (e instanceof ApiError && e.status === 404) {
        job = { ...job, state: "interrupted" };
        continue;
      }
      failures += 1;
      if (failures >= MAX_FAILURES) {
        failed(mine, e instanceof ApiError && e.status !== 0 ? e.message : t(LOST), "job");
        return;
      }
    }
  }
}

async function save(mine: number, exerciseId: string, job: DossierJob): Promise<void> {
  patch(mine, { phase: "saving", job });
  const result = await fetchDossierFile(genesisDeps.fileUrl(exerciseId, job.id));
  if (mine !== run) return;
  if ("saved" in result) saved(mine, result.saved);
  else failed(mine, "failed" in result ? result.failed : t(INTERRUPTED), "save");
}

/** Lance (ou rejoint) la genèse du dossier complet. Préalable manquant signalé par le serveur : la carte se retire,
 *  le préalable est rendu à l'appelant (qui ouvre sa vue et y désigne l'action attendue). */
export async function startDossierGenesis(
  exerciseId: string,
  number: number,
  name: string,
): Promise<{ missing?: DossierMissing }> {
  const mine = begin({ kind: "dossier", exerciseId, number, name, url: null });
  let job: DossierJob;
  try {
    job = await genesisDeps.start(exerciseId, number);
  } catch (e) {
    const missing = incomplete(e);
    if (missing) {
      if (mine === run) closeGenesis();
      return { missing };
    }
    failed(mine, e instanceof Error ? e.message : t("Le serveur n'a pas pu préparer le fichier. Réessayez dans un instant."), "job");
    return {};
  }
  if (mine !== run) return {};
  patch(mine, { job, phase: "running", startedAt: genesisDeps.now() - job.elapsed_s * 1000 });
  void follow(mine, exerciseId, number, job);
  return {};
}

/** Prépare un document séparé du menu (fiche pays, mise en ambiance, OPO) avec la carte allégée. */
export async function startDocumentGenesis(
  kind: Exclude<GenesisKind, "dossier">,
  exerciseId: string,
  url: string,
  name: string,
): Promise<{ missing?: DossierMissing }> {
  const mine = begin({ kind, exerciseId, number: 1, name, url });
  patch(mine, { phase: "running" });
  const result = await fetchDossierFile(url);
  if (mine !== run) return {};
  if ("saved" in result) saved(mine, result.saved);
  else if ("missing" in result) {
    closeGenesis();
    return { missing: result.missing };
  } else failed(mine, result.failed, "job");
  return {};
}

/** « Réessayer » : réenregistre un fichier déjà produit, sinon relance la genèse (ou la préparation du document). */
export async function retryGenesis(): Promise<{ missing?: DossierMissing }> {
  const genesis = useGenesis.getState().genesis;
  if (!genesis || genesis.phase !== "failed") return {};
  if (genesis.failedAt === "save" && genesis.job) {
    const mine = begin(genesis, genesis.job);
    await save(mine, genesis.exerciseId, genesis.job);
    return {};
  }
  if (genesis.kind === "dossier") return startDossierGenesis(genesis.exerciseId, genesis.number, genesis.name);
  return startDocumentGenesis(genesis.kind, genesis.exerciseId, genesis.url ?? "", genesis.name);
}

/** Rattachement à la genèse du dossier ouvert (page rechargée, dossier rouvert) : une genèse en cours est suivie
 *  de nouveau ; une genèse achevée dont le fichier n'a pas été enregistré l'est. Rien sinon. */
export async function reattachGenesis(exerciseId: string, name: string): Promise<void> {
  const current = useGenesis.getState().genesis;
  if (current && current.exerciseId === exerciseId && current.phase !== "saved" && current.phase !== "failed") return;
  if (genesisBusy(current)) return;
  let job: DossierJob | null;
  try {
    job = await genesisDeps.current(exerciseId);
  } catch {
    return;
  }
  if (!job || genesisBusy(useGenesis.getState().genesis)) return;
  const active = job.state === "queued" || job.state === "running";
  if (!active && !(job.state === "done" && !job.downloaded)) return;
  const mine = begin({ kind: "dossier", exerciseId, number: job.number, name, url: null }, job);
  void follow(mine, exerciseId, job.number, job);
}

/** Carte réduite en pastille, ou rouverte. */
export function setGenesisCompact(compact: boolean): void {
  const genesis = useGenesis.getState().genesis;
  if (genesis) useGenesis.setState({ genesis: { ...genesis, compact } });
}

/** Ferme la carte. Une genèse en cours n'est pas arrêtée côté serveur : la carte se rattache à la prochaine
 *  ouverture du dossier, et le fichier reste à enregistrer. */
export function closeGenesis(): void {
  run += 1;
  if (savedTimer) clearTimeout(savedTimer);
  savedTimer = null;
  useGenesis.setState({ genesis: null });
}
